import { useScrollAnim } from '../../hooks/use-scroll-anim'
import {
  TrendingUp, Users, Clock, DollarSign,
  Lightbulb, Globe, HelpCircle, FileSearch
} from 'lucide-react'
import { useI18n } from '../../i18n'

export function ValuePropSection() {
  const { ref: entRef, isVisible: entVisible } = useScrollAnim()
  const { ref: userRef, isVisible: userVisible } = useScrollAnim()
  const { t } = useI18n()

  const enterpriseValues = [
    { icon: DollarSign, title: t('显著降低运营成本', 'Cut Operating Costs'), description: t('AI管家7x24在线应答，替代大量重复性咨询工作，企业人力成本降低40%以上', 'AI Butler answers 24/7, replacing repetitive queries. Labor costs reduced by 40%+'), stat: '40%+', statLabel: t('成本降低', 'Cost Down') },
    { icon: TrendingUp, title: t('大幅提升业务效率', 'Boost Efficiency'), description: t('员工即时获取精准业务答案，不再翻找文档或等待同事回复，决策速度翻倍', 'Instant accurate answers — no more digging through docs or waiting for colleagues. Decision speed doubled'), stat: '2x', statLabel: t('效率提升', 'Speed Up') },
    { icon: Users, title: t('知识沉淀永不流失', 'Knowledge Preserved'), description: t('企业经验与知识不再依赖个人记忆，AI管家让组织智慧持续积累、人人可及', 'Enterprise knowledge no longer depends on individual memory. Organizational wisdom accumulates and is accessible to all'), stat: '100%', statLabel: t('知识留存', 'Retention') },
    { icon: Clock, title: t('规则更新即时生效', 'Instant Policy Updates'), description: t('业务制度变更后AI管家同步更新，告别"旧政策还在用"的信息滞后困局', 'Policy changes sync instantly. No more "still using the old rules" lag'), stat: '<1min', statLabel: t('同步速度', 'Sync Speed') },
  ]

  const userValues = [
    { icon: HelpCircle, title: t('随时随地，秒级响应', 'Instant Response Anytime'), description: t('不用翻文档、找同事、等回复，任何业务问题AI管家即刻给出精准答案', 'No more searching docs, asking colleagues, or waiting. Get precise answers instantly') },
    { icon: FileSearch, title: t('精准定位所需信息', 'Pinpoint Information'), description: t('智能检索企业知识库，从海量文档中精确提取你需要的那个答案，而非一堆链接', 'Smart retrieval extracts the exact answer you need — not a pile of links') },
    { icon: Globe, title: t('浏览企业专属官网', 'Browse Enterprise Websites'), description: t('在PrimeBook AI平台上发现并浏览各企业一键生成的专属官网，直观感受AI管家赋能成果', 'Discover and browse enterprise websites generated on PrimeBook AI. See AI Butler empowerment in action') },
    { icon: Lightbulb, title: t('深度推理复杂问题', 'Deep Reasoning'), description: t('DeepSeek推理引擎面对复杂业务问题也能层层拆解，给出专业建议而非泛泛而谈', 'DeepSeek reasoning engine dissects complex problems layer by layer, delivering expert advice') },
  ]

  return (
    <section className="py-24 min-h-screen flex items-center" style={{ scrollSnapAlign: 'start' }}>
      <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Enterprise: stat cards */}
        <div ref={entRef} className={`mb-24 transition-all duration-700 ${entVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-12">
            <span className="inline-block px-3 py-1 text-xs font-medium text-violet-500 bg-violet-50 rounded-full mb-4">
              {t('企业价值', 'Enterprise Value')}
            </span>
            <h2 className="text-2xl md:text-4xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
              {t('企业为什么为 PrimeBook AI 买单', 'Why Enterprises Pay for PrimeBook AI')}
            </h2>
            <p className="text-sm text-gray-400 max-w-md">
              {t('每一分投入都有可量化的回报，降本增效立竿见影', 'Every cent invested has measurable returns. Cost reduction & efficiency gains are immediate')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {enterpriseValues.map((item, i) => (
              <div
                key={item.title}
                className={`group rounded-3xl bg-white border border-black/[0.04] p-6 text-center hover:shadow-lg hover:shadow-black/[0.04] hover:-translate-y-1 transition-all duration-500 ${
                  entVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`}
                style={{ transitionDelay: entVisible ? `${i * 100}ms` : '0ms' }}
              >
                <div className="h-10 w-10 rounded-xl bg-violet-50 flex items-center justify-center mx-auto mb-4">
                  <item.icon className="h-5 w-5 text-violet-500" />
                </div>
                <div className="text-3xl font-bold text-[#1a1a1a] mb-0.5 tracking-tight">{item.stat}</div>
                <div className="text-[11px] text-violet-400 font-medium mb-3">{item.statLabel}</div>
                <h3 className="text-xs font-semibold text-[#1a1a1a] mb-1.5">{item.title}</h3>
                <p className="text-[11px] text-gray-400 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Users: horizontal narrative layout */}
        <div ref={userRef} className={`transition-all duration-700 ${userVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="mb-12">
            <span className="inline-block px-3 py-1 text-xs font-medium text-sky-500 bg-sky-50 rounded-full mb-4">
              {t('用户价值', 'User Value')}
            </span>
            <h2 className="text-2xl md:text-4xl font-semibold text-[#1a1a1a] mb-3 tracking-tight">
              {t('用户为什么离不开 PrimeBook AI', 'Why Users Can\'t Leave PrimeBook AI')}
            </h2>
            <p className="text-sm text-gray-400 max-w-md">
              {t('不只是企业的工具，更是每个用户的工作利器', 'Not just an enterprise tool — a personal work weapon for every user')}
            </p>
          </div>

          <div className="space-y-3">
            {userValues.map((item, i) => (
              <div
                key={item.title}
                className={`group flex items-start gap-5 rounded-2xl bg-gradient-to-r from-sky-50/50 to-white border border-sky-100/50 p-6 hover:from-sky-50 hover:to-sky-50/30 transition-all duration-500 ${
                  userVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: userVisible ? `${i * 120}ms` : '0ms' }}
              >
                <div className="h-10 w-10 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                  <item.icon className="h-5 w-5 text-sky-500" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-[#1a1a1a] mb-1">{item.title}</h3>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
